import React, { useState, useEffect, useCallback } from 'react';
import Icon from './Icon';
import { toast } from './ToastProvider';
import { apiFetch } from '../config/api';
import '../styles/B2BTrainings.css';

const SPECIALIZATIONS = ['Fryzjer', 'Barber', 'Koloryzacja', 'Stylizacja', 'Keratyna', 'Kierownik'];
const WEEKDAYS = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Ndz'];
const HOURS = Array.from({length: 25}, (_, i) => {
  const h = (8 + Math.floor(i/2)).toString().padStart(2, '0');
  const m = i % 2 === 0 ? '00' : '30';
  return `${h}:${m}`;
}).filter(t => t <= '20:00');

const EMPTY_EMP = { firstName: '', lastName: '', specialization: 'Fryzjer', phone: '', email: '', color: '#1e3c72', active: true, schedule: {} };

const EmployeesPanel = ({ user }) => {
  const [employees, setEmployees] = useState([]);
  const [view, setView]           = useState('list');
  const [form, setForm]           = useState(EMPTY_EMP);
  const [editId, setEditId]       = useState(null);
  const [scheduleEmp, setScheduleEmp] = useState(null);

  const load = useCallback(async () => {
    if (!user?.salonName) return;
    try {
      const salons = await apiFetch('/salons?city=' + encodeURIComponent(user.salonCity || ''));
      const salon = salons.find(s => s.name === user.salonName);
      if (!salon) return;
      const data = await apiFetch(`/salons/${salon.id}/employees?all=true`);
      
      setEmployees((data || []).map(e => {
        let phone = e.phone || '';
        let digits = phone.replace(/\D/g, '');
        if (digits.startsWith('48') && digits.length >= 11) digits = digits.substring(2);
        if (digits.length >= 9) {
            phone = `+48 ${digits.substring(0,3)} ${digits.substring(3,6)} ${digits.substring(6,9)}`;
        }
        
        return {
          ...e,
          firstName: e.first_name,
          lastName: e.last_name,
          id: e.id,
          active: e.active !== false,
          schedule: e.schedule || {},
          phone: phone
        };
      }));
    } catch { }
  }, [user?.salonName, user?.salonCity]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName) { toast.error('Podaj imię i nazwisko'); return; }
    try {
      const salons = await apiFetch('/salons?city=' + encodeURIComponent(user.salonCity || ''));
      const salon = salons.find(s => s.name === user.salonName);
      if (!salon) { toast.error('Salon nie znaleziony'); return; }
      
      if (editId !== null) {
        await apiFetch(`/salons/${salon.id}/employees/${editId}`, {
          method: 'PUT',
          body: JSON.stringify(form),
        });
        toast.success('Zaktualizowano pracownika');
      } else {
        await apiFetch(`/salons/${salon.id}/employees`, {
          method: 'POST',
          body: JSON.stringify(form),
        });
        toast.success('Dodano pracownika');
      }
      setForm(EMPTY_EMP); setEditId(null); setView('list');
      
      setTimeout(() => {
        load();
        window.location.reload();
      }, 1000);
    } catch (err) {
      toast.error(err.message || 'Błąd podczas zapisywania');
    }
  };

  const toggleActive = async (id) => {
    const emp = employees.find(e => e.id === id);
    if (!emp) return;
    try {
      const salons = await apiFetch('/salons?city=' + encodeURIComponent(user.salonCity || ''));
      const salon = salons.find(s => s.name === user.salonName);
      if (!salon) return;
      
      const newActiveState = !emp.active;
      
      await apiFetch(`/salons/${salon.id}/employees/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ 
          firstName: emp.firstName,
          lastName: emp.lastName,
          specialization: emp.specialization,
          phone: emp.phone,
          email: emp.email,
          color: emp.color,
          active: newActiveState 
        }),
      });
      toast.success(newActiveState ? 'Pracownik aktywowany' : 'Pracownik dezaktywowany');
      load();
    } catch (err) {
      toast.error('Błąd podczas zmiany statusu');
    }
  };

  const deleteEmp = async (id, name) => {
    if (!window.confirm(`Usunąć ${name}?`)) return;
    try {
      const salons = await apiFetch('/salons?city=' + encodeURIComponent(user.salonCity || ''));
      const salon = salons.find(s => s.name === user.salonName);
      if (!salon) return;
      await apiFetch(`/salons/${salon.id}/employees/${id}`, { method: 'DELETE' });
      load(); toast.info('Usunięto pracownika');
    } catch { }
  };

  const saveSchedule = async (empId, schedule) => {
    const emp = employees.find(e => e.id === empId);
    if (!emp) return;
    try {
      const salons = await apiFetch('/salons?city=' + encodeURIComponent(user.salonCity || ''));
      const salon = salons.find(s => s.name === user.salonName);
      if (!salon) return;
      await apiFetch(`/salons/${salon.id}/employees/${empId}`, {
        method: 'PUT',
        body: JSON.stringify({ ...emp, schedule }),
      });
      load(); setView('list'); setScheduleEmp(null); toast.success('Grafik zapisany!');
      
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch { }
  };

  const COLORS = ['#1e3c72','#7c3aed','#059669','#dc2626','#d97706','#0891b2','#db2777','#4f46e5'];

  if (view === 'schedule' && scheduleEmp) {
    return <ScheduleEditor emp={scheduleEmp} onSave={saveSchedule} onBack={() => { setView('list'); setScheduleEmp(null); }} />;
  }

  if (view === 'add' || view === 'edit') {
    return (
      <div className="emp-form-wrap">
        <div className="emp-form-header">
          <button className="emp-back-btn" onClick={() => { setView('list'); setForm(EMPTY_EMP); setEditId(null); }}>
            <Icon name="back" style={{marginRight:'8px'}} />Wróć
          </button>
          <h3>{editId ? 'Edytuj pracownika' : 'Dodaj pracownika'}</h3>
        </div>
        <form onSubmit={handleSave} className="emp-form">
          <div className="emp-form-grid">
            <div className="trainings-field">
              <label>Imię *</label>
              <input className="trainings-input" value={form.firstName} onChange={e => setForm(f => ({...f, firstName: e.target.value}))} placeholder="Jan" required />
            </div>
            <div className="trainings-field">
              <label>Nazwisko *</label>
              <input className="trainings-input" value={form.lastName} onChange={e => setForm(f => ({...f, lastName: e.target.value}))} placeholder="Kowalski" required />
            </div>
            <div className="trainings-field">
              <label>Specjalizacja</label>
              <select className="trainings-input" value={form.specialization} onChange={e => setForm(f => ({...f, specialization: e.target.value}))}>
                {SPECIALIZATIONS.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="trainings-field">
              <label>Telefon *</label>
              <input 
                className="trainings-input" 
                type="tel"
                value={form.phone || '+48 '} 
                onChange={e => {
                  let val = e.target.value;
                  if (!val.includes('+48')) val = '+48 ' + val;
                  
                  let digits = val.replace(/\D/g, '');
                  if (digits.startsWith('48')) digits = digits.substring(2);
                  digits = digits.substring(0, 9);
                  
                  let formatted = '+48';
                  if (digits.length > 0) formatted += ' ' + digits.substring(0, 3);
                  if (digits.length > 3) formatted += ' ' + digits.substring(3, 6);
                  if (digits.length > 6) formatted += ' ' + digits.substring(6, 9);
                  
                  setForm(f => ({...f, phone: formatted === '+48' ? '+48 ' : formatted}));
                }} 
                placeholder="+48 123 456 789"
                pattern="^\+48\s\d{3}\s\d{3}\s\d{3}$"
                title="Wymagany format: +48 XXX XXX XXX"
                required
              />
            </div>
            <div className="trainings-field">
              <label>Email</label>
              <input className="trainings-input" type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="jan@salon.pl" />
            </div>
            <div className="trainings-field">
              <label>Kolor w grafiku</label>
              <div className="emp-color-picker">
                {COLORS.map(col => (
                  <div key={col} className={`emp-color-swatch ${form.color === col ? 'selected' : ''}`}
                    style={{background: col}} onClick={() => setForm(f => ({...f, color: col}))} />
                ))}
              </div>
            </div>
          </div>
          <div className="trainings-form-actions">
            <button type="button" className="trainings-cancel-btn" onClick={() => { setView('list'); setForm(EMPTY_EMP); setEditId(null); }}>Anuluj</button>
            <button type="submit" className="trainings-submit-btn">
              <Icon name={editId ? 'check' : 'add'} style={{marginRight:'7px'}} />{editId ? 'Zapisz zmiany' : 'Dodaj pracownika'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  const active = employees.filter(e => e.active);
  const inactive = employees.filter(e => !e.active);

  return (
    <div className="emp-wrapper">
      <div className="emp-header">
        <div className="emp-header-left">
          <div className="emp-header-icon"><Icon name="users" color="white" size="xl" /></div>
          <div>
            <h2>Zespół pracowników</h2>
            <p>{active.length} aktywnych • {inactive.length} nieaktywnych</p>
          </div>
        </div>
        <button className="trainings-new-btn" onClick={() => { setForm(EMPTY_EMP); setEditId(null); setView('add'); }}>
          <Icon name="add" style={{marginRight:'7px'}} />Dodaj pracownika
        </button>
      </div>

      {employees.length === 0 ? (
        <div className="trainings-empty">
          <Icon name="users" color="#d1d5db" size="3x" />
          <h3>Brak pracowników</h3>
          <p>Dodaj pracowników, aby zarządzać grafikiem i rezerwacjami</p>
          <button className="trainings-submit-btn" onClick={() => setView('add')}>
            <Icon name="add" style={{marginRight:'7px'}} />Dodaj pierwszego pracownika
          </button>
        </div>
      ) : (
        <>
          {active.length > 0 && (
            <div className="emp-section">
              <div className="emp-section-label"><Icon name="check" color="#10b981" style={{marginRight:'8px'}} />Aktywni ({active.length})</div>
              <div className="emp-grid">
                {active.map(emp => <EmpCard key={emp.id} emp={emp}
                  onEdit={() => { setForm({...emp}); setEditId(emp.id); setView('edit'); }}
                  onSchedule={() => { setScheduleEmp(emp); setView('schedule'); }}
                  onToggle={() => toggleActive(emp.id)}
                  onDelete={() => deleteEmp(emp.id, `${emp.firstName} ${emp.lastName}`)} />)}
              </div>
            </div>
          )}
          {inactive.length > 0 && (
            <div className="emp-section" style={{marginTop:'24px'}}>
              <div className="emp-section-label" style={{color:'#9ca3af'}}><Icon name="close" color="#9ca3af" style={{marginRight:'8px'}} />Nieaktywni ({inactive.length})</div>
              <div className="emp-grid" style={{opacity:'0.65'}}>
                {inactive.map(emp => <EmpCard key={emp.id} emp={emp}
                  onEdit={() => { setForm({...emp}); setEditId(emp.id); setView('edit'); }}
                  onSchedule={() => { setScheduleEmp(emp); setView('schedule'); }}
                  onToggle={() => toggleActive(emp.id)}
                  onDelete={() => deleteEmp(emp.id, `${emp.firstName} ${emp.lastName}`)} />)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const EmpCard = ({ emp, onEdit, onSchedule, onToggle, onDelete }) => {
  const initials = `${emp.firstName[0]}${emp.lastName[0]}`.toUpperCase();
  const schedule = emp.schedule || {};
  const workDays = Object.keys(schedule).length;
  return (
    <div className="emp-card">
      <div className="emp-card-top">
        <div className="emp-card-avatar" style={{background: emp.color || '#1e3c72'}}>{initials}</div>
        <div className="emp-card-info">
          <strong>{emp.firstName} {emp.lastName}</strong>
          <span className="emp-card-spec">{emp.specialization}</span>
          {workDays > 0 && (
            <span className="emp-card-schedule-badge">
              <Icon name="calendar" style={{marginRight:'4px',fontSize:'0.75em'}} />
              {workDays} {workDays === 1 ? 'dzień' : workDays < 5 ? 'dni' : 'dni'}/tydz.
            </span>
          )}
        </div>
        <span className={`emp-status-dot ${emp.active ? 'active' : 'inactive'}`} title={emp.active ? 'Aktywny' : 'Nieaktywny'} />
      </div>
      {(emp.phone || emp.email) && (
        <div className="emp-card-contacts">
          {emp.phone && <span className="emp-card-contact"><Icon name="phone" style={{marginRight:'4px'}} />{emp.phone}</span>}
          {emp.email && <span className="emp-card-contact"><Icon name="email" style={{marginRight:'4px'}} />{emp.email}</span>}
        </div>
      )}
      <div className="emp-card-actions">
        <button className="emp-action-btn primary" onClick={onSchedule} title="Grafik">
          <Icon name="calendar" style={{marginRight:'5px'}} />Grafik
        </button>
        <button className="emp-action-btn" onClick={onEdit} title="Edytuj"><Icon name="edit" /></button>
        <button className={`emp-action-btn ${emp.active ? 'btn-warn' : 'btn-ok'}`} onClick={onToggle} title={emp.active ? 'Dezaktywuj' : 'Aktywuj'}>
          <Icon name={emp.active ? 'close' : 'check'} />
        </button>
        <button className="emp-action-btn btn-danger" onClick={onDelete} title="Usuń"><Icon name="delete" /></button>
      </div>
    </div>
  );
};

const ScheduleEditor = ({ emp, onSave, onBack }) => {
  const [schedule, setSchedule] = useState(emp.schedule || {});

  const toggleDay = (day) => {
    setSchedule(s => {
      const next = { ...s };
      if (next[day]) { delete next[day]; }
      else { next[day] = { from: '09:00', to: '17:00' }; }
      return next;
    });
  };

  const setHour = (day, field, val) => {
    setSchedule(s => ({ ...s, [day]: { ...s[day], [field]: val } }));
  };

  return (
    <div className="emp-schedule-wrap">
      <div className="emp-form-header">
        <button className="emp-back-btn" onClick={onBack}><Icon name="back" style={{marginRight:'8px'}} />Wróć</button>
        <h3>Grafik - {emp.firstName} {emp.lastName}</h3>
      </div>

      <div className="schedule-emp-info">
        <div className="schedule-emp-avatar" style={{background: emp.color || '#1e3c72'}}>
          {emp.firstName[0]}{emp.lastName[0]}
        </div>
        <div>
          <strong>{emp.firstName} {emp.lastName}</strong>
          <span>{emp.specialization}</span>
        </div>
      </div>

      <div className="schedule-grid">
        {WEEKDAYS.map((day, idx) => {
          const active = !!schedule[day];
          return (
            <div key={day} className={`schedule-day ${active ? 'active' : ''}`}>
              <div className="schedule-day-header">
                <label className="schedule-toggle">
                  <input type="checkbox" checked={active} onChange={() => toggleDay(day)} />
                  <span className="schedule-toggle-slider" />
                </label>
                <span className="schedule-day-name">{day}</span>
                {idx >= 5 && <span className="schedule-weekend-badge">Weekend</span>}
              </div>
              {active && (
                <div className="schedule-hours">
                  <div className="schedule-hour-group">
                    <label>Od</label>
                    <select value={schedule[day].from} onChange={e => setHour(day, 'from', e.target.value)} className="schedule-hour-select">
                      {HOURS.map(h => <option key={h}>{h}</option>)}
                    </select>
                  </div>
                  <div className="schedule-hour-sep">-</div>
                  <div className="schedule-hour-group">
                    <label>Do</label>
                    <select value={schedule[day].to} onChange={e => setHour(day, 'to', e.target.value)} className="schedule-hour-select">
                      {HOURS.filter(h => h > schedule[day].from).map(h => <option key={h}>{h}</option>)}
                    </select>
                  </div>
                </div>
              )}
              {!active && <div className="schedule-day-off">Wolne</div>}
            </div>
          );
        })}
      </div>

      <div className="schedule-summary">
        <Icon name="calendar" color="#1e3c72" style={{marginRight:'8px'}} />
        Pracuje: <strong style={{margin:'0 6px'}}>{Object.keys(schedule).length}</strong> dni w tygodniu
        {Object.keys(schedule).length > 0 && (
          <span style={{marginLeft:'8px',color:'#9ca3af'}}>
            ({Object.entries(schedule).map(([d,h]) => `${d} ${h.from}-${h.to}`).join(', ')})
          </span>
        )}
      </div>

      <div className="trainings-form-actions">
        <button className="trainings-cancel-btn" onClick={onBack}>Anuluj</button>
        <button className="trainings-submit-btn" onClick={() => onSave(emp.id, schedule)}>
          <Icon name="check" style={{marginRight:'7px'}} />Zapisz grafik
        </button>
      </div>
    </div>
  );
};

export default EmployeesPanel;